import {
    Component,
    ElementRef,
    ViewChild,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { BaseForm } from '../../../../base-form';
import { TareasBioFormDefinition } from './form-control/form-definition';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';
import moment from 'moment';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import type { TareasBioModel } from '../../../../../models/tareas/tareas-bio.model';
import type { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import type { MantenimientoModel } from '../../../../../models/trabajadores/mantenimiento.model';
import type { FincasModel } from '../../../../../models/form-common/fincas.module';
import {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormValue,
    FormType,
    DropdownData,
} from '@fitogest/reactive-form-builder/interfaces';
import { TareasBioEnvironmentDefinition } from '../environment-variables';

@Component({
    selector: 'app-tareas-bio-pulverizacion',
    standalone: false,
    templateUrl: './tareas_bio-form.component.html',
    styleUrls: ['./tareas_bio-form.component.scss'],
})
export class TareasBioFormComponent extends BaseForm<TareasBioModel> implements OnInit, OnDestroy {
    @ViewChild('imagenCapturada') imagenCapturada: ElementRef = new ElementRef('');

    public env: TareasBioEnvironmentDefinition = new TareasBioEnvironmentDefinition();
    public form!: TareasBioFormDefinition;
    public override model: TareasBioModel = {};
    public formReady = false;
    public loadingData = true;
    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    public showRecetaTareaBio = false;
    public isGeneratingCanvas = false;
    public receta: TareasBioModel = {};
    public parcela: ParcelasModel = {
        id: '',
        nombre: '',
        superficie: '',
        finca: '',
        sector: ''
    };

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public tareasApi: TareasApiService,
        public trabajadores: TrabajadoresApiService,
        public override router: Router,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.bio.PUT,
            tareasApi.bio.POST,
            'bio',
            'Actualizar Tarea Bio',
            'Realizar Tarea Bio',
            'Duplicar Tarea Bio',
        );
    }

    public async ngOnInit(): Promise<void> {
        if (this.route.snapshot.paramMap.get('deCalendario')?.toString() === 'true') {
            this.pathToGoBack = 'tratamientos-control';
        }

        this.softInit(this.getType());
        this.model = { ...this.getModel() };
        this.form = new TareasBioFormDefinition(this.getType(), this.model);
        this.form.model = this.model;

        await this.initFormData();

        this.formReady = true;
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public formChanges(
        formModel: ReactiveFormValue<TareasBioModel>,
        tag: ReactiveFormChangedField<TareasBioModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        } as TareasBioModel;
        this.model = this.form.model as TareasBioModel;

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }
    }

    public onInternalLifecycleEvent(event: ReactiveFormLifecycleEvent<TareasBioModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        this.form.model = {
            ...this.form.model,
            ...event.value,
        } as TareasBioModel;
        this.model = this.form.model as TareasBioModel;
        this.updateHorasTotales();
    }

    public generarReceta(): void {
        this.receta = {
            ...this.model,
            finca_nombre: this.resolveSelectedDropdownEntity(
                this.form.lists.fincas,
                this.form.model.id_finca ?? this.model.id_finca,
            )?.nombre
                ?? this.model.finca_nombre
                ?? '',
            dosis1: this.concatDoseAndUnit(this.model.dosis1, this.model.ud_real_1),
            dosis2: this.concatDoseAndUnit(this.model.dosis2, this.model.ud_real_2),
            dosis3: this.concatDoseAndUnit(this.model.dosis3, this.model.ud_real_3),
            fecha: this.model.fecha ? moment(this.model.fecha).format('LL') : '',
        };

        this.showRecetaTareaBio = true;
        const elements = document.getElementsByClassName('p-dialog-content');

        setTimeout(() => {
            if (elements[0]) {
                elements[0].scrollTop = 0;
            }
        }, 100);
    }

    public imprimirReceta(): void {
        this.isGeneratingCanvas = true;
        const config = {
            canvasScale: 2,
            width: 220,
        };
        const canvasElement = document.getElementById('html2canvasDiv');

        html2canvas(canvasElement ?? new HTMLElement(), { scale: config.canvasScale, logging: true }).then(canvas => {
            this.isGeneratingCanvas = false;

            const pdf = new jsPDF('l', 'mm', 'a4');
            const data = canvas.toDataURL('image/png');

            pdf.addImage(data, 'png', 10, 10, config.width, canvas.height / (canvas.width / config.width), 'alias', 'FAST');
            pdf.save('Bio.pdf');

            return;
        }).catch((error: unknown) => {
            this.isGeneratingCanvas = false;
            console.log('catch en imprimirReceta: ' + error);
        });
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        return pool([
            build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true),
            build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true),
            build(this.fincasApi.parcelasGET, this.form.lists.parcelas, 'nombre', true),
            build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }),
            build(this.trabajadores.trabajadoresGET, this.form.lists.trabajadores,
                (it: MantenimientoModel) => `${it.nombre} ${it.apellidos || ''}`,
                false,
                true),
            build(this.trabajadores.maquinariaGET, this.form.lists.maquinarias, 'nombre', true),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.applySingleFincaDefault();
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();
            console.error('Error loading tareas bio form data.', error);

            return true;
        });
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        this.clearLocationChildren(['id_finca', 'id_sector', 'ids_parcelas']);

        if (!selectedClientId || this.env.appName === 'agricar') {
            return;
        }

        if (this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            this.dropdownLoadTrigger.next();
            return;
        }

        this.reloadClientDropdowns(selectedClientId);
    }

    private reloadClientDropdowns(selectedClientId: string): void {
        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.loadingData = true;
        this.resetExternalDropdowns();
        this.dropdownLoadTrigger.next();

        pool([
            buildParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
            buildParams(this.fincasApi.sectoresGET, { id_cliente: selectedClientId }, this.form.lists.sectores, 'nombre', true),
            buildParams(this.fincasApi.parcelasGET, { id_cliente: selectedClientId }, this.form.lists.parcelas, 'nombre', true),
        ], () => {
            this.loadingData = false;
            this.form.lists.fincas.filtered = this.form.lists.fincas.values;
            this.form.lists.sectores.filtered = [];
            this.form.lists.parcelas.values = this.form.lists.parcelas.values.filter((it) => it.label !== '...');
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.finalizeExternalDropdowns();
            this.isReloadingClientDropdowns = false;
            this.dropdownLoadTrigger.next();

            return;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.isReloadingClientDropdowns = false;
            this.dropdownLoadTrigger.next();
            console.error('Error reloading tareas bio client dropdowns.', error);
        });
    }

    private clearLocationChildren(fieldNames: string[]): void {
        const patch = fieldNames.reduce((result, fieldName) => ({ ...result, [fieldName]: null }), {} as Record<string, null>);

        this.form.model = {
            ...this.form.model,
            ...patch,
        };
        this.model = this.form.model as TareasBioModel;
        this.formBuilderRef?.updateForm(patch, true);
        this.dropdownLoadTrigger.next();
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            horas_totales: horasTotales,
        };
        this.model = this.form.model as TareasBioModel;

        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private applySingleFincaDefault(): void {
        if (this.getType() !== FormType.CREATION || this.form.lists.fincas.filtered.length !== 2) {
            return;
        }

        const finca = this.form.lists.fincas.filtered[1]?.value as FincasModel | null;

        if (!finca?.id) {
            return;
        }

        this.model = {
            ...this.model,
            id_finca: finca.id,
        };
        this.form.model = this.model;
    }

    private concatDoseAndUnit(dose: unknown, unit: unknown): string {
        return [dose, unit]
            .map((value) => String(value ?? '').trim())
            .filter((value) => value.length > 0)
            .join(' ');
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        const tokens = this.getReactiveValueTokens(selectedValue);

        if (tokens.length === 0) {
            return null;
        }

        return dropdownData.values
            .map((option) => this.getDropdownOptionEntity<TEntity>(option))
            .find((entity): entity is TEntity => entity !== null && tokens.includes(this.getReactiveValueId(entity)))
            ?? null;
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (typeof value === 'object' && value !== null) {
            const entityId = this.getReactiveValueId(value);

            return entityId.length > 0 ? [entityId] : [];
        }

        return String(value ?? '')
            .split(';')
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    private getDropdownOptionEntity<TEntity extends object>(
        option: { readonly value: TEntity | null; readonly raw?: unknown },
    ): TEntity | null {
        if (this.isRecord(option.raw)) {
            return option.raw as unknown as TEntity;
        }

        if (this.isRecord(option.value)) {
            return option.value as unknown as TEntity;
        }

        return null;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
